/* eslint-disable max-lines */
/**
 * Relationship Matrix Visualization
 * Heatmap view for bulk relationship operations
 */

import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Alert,
  FormControlLabel,
  Switch,
  Slider,
  TextField,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Divider,
  Collapse,
} from '@mui/material';
import {
  SelectAll as SelectAllIcon,
  CheckBox as CheckBoxIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import useTokenExaminerStore from '../stores/tokenExaminerStore';
import {
  getFaanaHealthColor,
  getConfidenceOpacity,
} from '../utils/faanaScoring';
import { getContrastText } from '../utils/colorContrast';
import RelationshipDetailPanel from '../components/RelationshipDetailPanel';

const CELL_SIZE = 40;
const MARGIN = { top: 120, right: 100, bottom: 60, left: 120 };

// eslint-disable-next-line max-lines-per-function
function RelationshipMatrix() {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [selectedRelationship, setSelectedRelationship] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [showConfirmedOnly, setShowConfirmedOnly] = useState(false);
  const [healthThreshold, setHealthThreshold] = useState(0);
  const [showEmptyCells, setShowEmptyCells] = useState(false); // Default to hiding empty cells
  const [zoomLevel, setZoomLevel] = useState(1);
  const zoomBehaviorRef = useRef(null);
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [maxEntities, setMaxEntities] = useState(20);
  const [sortBy, setSortBy] = useState('connections'); // 'name', 'connections', 'health'
  const [controlsExpanded, setControlsExpanded] = useState(true);

  const {
    _relationships,
    entities,
    _selectedEntity,
    confirmRelationship,
    rejectRelationship,
    _selectRelationships,
    getFilteredRelationships,
  } = useTokenExaminerStore();

  const filteredRelationships = getFilteredRelationships()
    .filter(rel => {
      if (showConfirmedOnly && !rel.isConfirmed) return false;
      if ((rel.overallHealth || 0) < healthThreshold) return false;
      return true;
    });

  // Enhanced filtering and sorting logic
  const processedEntities = useMemo(() => {
    // Get all unique entities
    const allEntities = Array.from(new Set([
      ...filteredRelationships.map(r => r.sourceEntity?.crossEntityHash || r.sourceEntityHash),
      ...filteredRelationships.map(r => r.targetEntity?.crossEntityHash || r.targetEntityHash),
    ])).filter(Boolean).map(hash => {
      const entity = Array.from(entities.values()).find(e => e.crossEntityHash === hash);
      if (!entity) {
        const rel = filteredRelationships.find(r =>
          r.sourceEntity?.crossEntityHash === hash || r.targetEntity?.crossEntityHash === hash
        );
        if (rel?.sourceEntity?.crossEntityHash === hash) return rel.sourceEntity;
        if (rel?.targetEntity?.crossEntityHash === hash) return rel.targetEntity;
      }
      return entity || { crossEntityHash: hash, name: `Entity ${hash.slice(0, 8)}`, type: 'UNKNOWN' };
    });

    // Apply filters
    let filtered = allEntities;

    // Entity type filter
    if (entityTypeFilter !== 'all') {
      filtered = filtered.filter(e => e.type === entityTypeFilter);
    }

    // Search filter
    if (searchFilter) {
      const search = searchFilter.toLowerCase();
      filtered = filtered.filter(e =>
        e.name.toLowerCase().includes(search) ||
        e.type.toLowerCase().includes(search)
      );
    }

    // Calculate metrics for sorting
    const entitiesWithMetrics = filtered.map(entity => {
      const entityRelationships = filteredRelationships.filter(r =>
        (r.sourceEntity?.crossEntityHash || r.sourceEntityHash) === entity.crossEntityHash ||
        (r.targetEntity?.crossEntityHash || r.targetEntityHash) === entity.crossEntityHash
      );

      const avgHealth = entityRelationships.length > 0
        ? entityRelationships.reduce((sum, r) => sum + (r.overallHealth || 0), 0) / entityRelationships.length
        : 0;

      return {
        ...entity,
        connectionCount: entityRelationships.length,
        averageHealth: avgHealth,
      };
    });

    // Sort
    const sorted = [...entitiesWithMetrics].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'connections':
          return b.connectionCount - a.connectionCount;
        case 'health':
          return b.averageHealth - a.averageHealth;
        default:
          return 0;
      }
    });

    // Limit entities
    return sorted.slice(0, maxEntities);
  }, [filteredRelationships, entities, entityTypeFilter, searchFilter, sortBy, maxEntities]);

  const uniqueEntities = processedEntities;

  // Create matrix data - memoized to ensure proper recalculation
  const matrixData = useMemo(() => {
    const data = [];
    uniqueEntities.forEach((sourceEntity, i) => {
      uniqueEntities.forEach((targetEntity, j) => {
        if (i !== j) { // Don't show self-relationships
          const relationship = filteredRelationships.find(rel =>
            ((rel.sourceEntity?.crossEntityHash || rel.sourceEntityHash) === sourceEntity.crossEntityHash &&
             (rel.targetEntity?.crossEntityHash || rel.targetEntityHash) === targetEntity.crossEntityHash) ||
            ((rel.sourceEntity?.crossEntityHash || rel.sourceEntityHash) === targetEntity.crossEntityHash &&
             (rel.targetEntity?.crossEntityHash || rel.targetEntityHash) === sourceEntity.crossEntityHash)
          );

          // Only include this cell if showEmptyCells is true OR there's a relationship
          if (showEmptyCells || relationship) {
            data.push({
              sourceIndex: i,
              targetIndex: j,
              sourceEntity,
              targetEntity,
              relationship,
              key: `${i}-${j}`,
            });
          }
        }
      });
    });
    return data;
  }, [uniqueEntities, filteredRelationships, showEmptyCells]);

  // Update dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Initialize zoom behavior separately (once)
  useEffect(() => {
    if (!svgRef.current || zoomBehaviorRef.current) return;

    const svg = d3.select(svgRef.current);

    // Create zoom behavior
    zoomBehaviorRef.current = d3.zoom()
      .scaleExtent([0.5, 5])
      .on('zoom', (event) => {
        const zoomGroup = svg.select('.zoom-group');
        if (!zoomGroup.empty()) {
          zoomGroup.attr('transform', event.transform);
          setZoomLevel(event.transform.k);
        }
      });

    // Apply zoom to svg
    svg.call(zoomBehaviorRef.current);

    // Set initial transform
    svg.call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
  }, []);

  // Render matrix
  useEffect(() => {
    if (!dimensions.width || !dimensions.height || uniqueEntities.length === 0) return;

    const svg = d3.select(svgRef.current);

    // Clear existing content but preserve zoom behavior
    let zoomGroup = svg.select('.zoom-group');
    if (zoomGroup.empty()) {
      zoomGroup = svg.append('g').attr('class', 'zoom-group');
    } else {
      zoomGroup.selectAll('*').remove();
    }


    svg.attr('width', dimensions.width).attr('height', dimensions.height);

    // Update zoom extent when dimensions change
    if (zoomBehaviorRef.current) {
      zoomBehaviorRef.current.extent([[0, 0], [dimensions.width, dimensions.height]]);
    }

    const container = zoomGroup.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Add row labels (source entities)
    const rowLabels = container.selectAll('.row-label')
      .data(uniqueEntities)
      .enter().append('g')
      .attr('class', 'row-label')
      .attr('transform', (d, i) => `translate(-10, ${i * CELL_SIZE + CELL_SIZE / 2})`);

    rowLabels.append('text')
      .attr('text-anchor', 'end')
      .attr('dy', '0.35em')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .text(d => d.name.length > 15 ? d.name.substring(0, 15) + '...' : d.name);

    // Add column labels (target entities)
    const colLabels = container.selectAll('.col-label')
      .data(uniqueEntities)
      .enter().append('g')
      .attr('class', 'col-label')
      .attr('transform', (d, i) => `translate(${i * CELL_SIZE + CELL_SIZE / 2}, -10)`);

    colLabels.append('text')
      .attr('text-anchor', 'start')
      .attr('dy', '0.35em')
      .attr('transform', 'rotate(-45)')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .text(d => d.name.length > 15 ? d.name.substring(0, 15) + '...' : d.name);

    // Add matrix cells
    const cells = container.selectAll('.matrix-cell')
      .data(matrixData)
      .enter().append('g')
      .attr('class', 'matrix-cell')
      .attr('transform', d => `translate(${d.targetIndex * CELL_SIZE}, ${d.sourceIndex * CELL_SIZE})`);

    // Cell backgrounds
    cells.append('rect')
      .attr('width', CELL_SIZE)
      .attr('height', CELL_SIZE)
      .attr('fill', d => {
        if (!d.relationship) return '#f5f5f5';
        return getFaanaHealthColor(d.relationship.overallHealth || 0);
      })
      .attr('stroke', '#fff')
      .attr('stroke-width', 1)
      .attr('opacity', d => {
        if (!d.relationship) return 0.3;
        return getConfidenceOpacity(d.relationship.confidence);
      })
      .style('cursor', 'pointer')
      .on('mouseenter', (event, d) => handleCellMouseEnter(event, d))
      .on('mouseleave', handleCellMouseLeave)
      .on('click', (event, d) => handleCellClick(event, d))
      .on('contextmenu', (event, d) => handleCellContextMenu(event, d));

    // Cell indicators
    cells.filter(d => d.relationship)
      .append('circle')
      .attr('cx', CELL_SIZE / 2)
      .attr('cy', CELL_SIZE / 2)
      .attr('r', d => d.relationship.isConfirmed ? 6 : 3)
      .attr('fill', d => d.relationship.isConfirmed ? '#4CAF50' : '#FF9800')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1)
      .style('pointer-events', 'none');

    // Selection indicators
    cells.filter(d => selectedCells.has(d.key))
      .append('rect')
      .attr('width', CELL_SIZE)
      .attr('height', CELL_SIZE)
      .attr('fill', 'none')
      .attr('stroke', '#2196F3')
      .attr('stroke-width', 3)
      .style('pointer-events', 'none');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions, matrixData, selectedCells, showConfirmedOnly, healthThreshold, uniqueEntities, showEmptyCells]);

  // Event handlers
  const handleCellMouseEnter = (event, d) => {
    setHoveredCell(d);
  };

  const handleCellMouseLeave = () => {
    setHoveredCell(null);
  };

  const handleCellClick = (event, d) => {
    if (event.shiftKey) {
      // Multi-select
      const newSelected = new Set(selectedCells);
      if (newSelected.has(d.key)) {
        newSelected.delete(d.key);
      } else {
        newSelected.add(d.key);
      }
      setSelectedCells(newSelected);
    } else if (d.relationship) {
      // Single select - show detail panel
      setSelectedRelationship(d.relationship);
    }
  };

  const handleCellContextMenu = (event, d) => {
    event.preventDefault();
    if (d.relationship) {
      setContextMenu({
        mouseX: event.clientX - 2,
        mouseY: event.clientY - 4,
        relationship: d.relationship,
      });
    }
  };

  const handleSelectAll = () => {
    const allCells = new Set(matrixData.filter(d => d.relationship).map(d => d.key));
    setSelectedCells(allCells);
  };

  const handleClearSelection = () => {
    setSelectedCells(new Set());
  };

  const handleBatchConfirm = async () => {
    const relationshipsToConfirm = Array.from(selectedCells)
      .map(key => matrixData.find(d => d.key === key)?.relationship)
      .filter(Boolean);

    for (const rel of relationshipsToConfirm) {
      if (!rel.isConfirmed) {
        await confirmRelationship(rel.id, 'batch-operation');
      }
    }

    setSelectedCells(new Set());
  };

  const handleBatchReject = async () => {
    const relationshipsToReject = Array.from(selectedCells)
      .map(key => matrixData.find(d => d.key === key)?.relationship)
      .filter(Boolean);

    for (const rel of relationshipsToReject) {
      await rejectRelationship(rel.id);
    }

    setSelectedCells(new Set());
  };

  const selectedCount = selectedCells.size;
  const confirmedCount = Array.from(selectedCells)
    .map(key => matrixData.find(d => d.key === key)?.relationship)
    .filter(rel => rel?.isConfirmed).length;

  // Zoom handlers
  const handleZoomIn = () => {
    if (!zoomBehaviorRef.current || !svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(zoomBehaviorRef.current.scaleBy, 1.2);
  };

  const handleZoomOut = () => {
    if (!zoomBehaviorRef.current || !svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(zoomBehaviorRef.current.scaleBy, 0.8);
  };

  const handleResetZoom = () => {
    if (!zoomBehaviorRef.current || !svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(
      zoomBehaviorRef.current.transform,
      d3.zoomIdentity
    );
  };

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Controls */}
      <Paper
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 1000,
          maxWidth: 340,
        }}
        elevation={3}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            pb: controlsExpanded ? 0 : 2,
            cursor: 'pointer',
          }}
          onClick={() => setControlsExpanded(!controlsExpanded)}
        >
          <Typography variant="subtitle1">
            Matrix Controls
          </Typography>
          <IconButton size="small">
            {controlsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={controlsExpanded}>
          <Box sx={{ px: 2, pb: 2 }}>
            {/* Filter Controls */}
            <Box sx={{ mb: 2 }}>
              <TextField
            fullWidth
            size="small"
            placeholder="Search entities..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />

          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>Entity Type</InputLabel>
            <Select
              value={entityTypeFilter}
              onChange={(e) => setEntityTypeFilter(e.target.value)}
              label="Entity Type"
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="PERSON">Person</MenuItem>
              <MenuItem value="ORGANIZATION">Organization</MenuItem>
              <MenuItem value="PROGRAM">Program</MenuItem>
              <MenuItem value="COMMUNITY">Community</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              label="Sort By"
            >
              <MenuItem value="connections">Connection Count</MenuItem>
              <MenuItem value="health">Average Health</MenuItem>
              <MenuItem value="name">Name</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Max Entities: {maxEntities} ({uniqueEntities.length} shown)
          </Typography>
          <Slider
            value={maxEntities}
            onChange={(e, value) => setMaxEntities(value)}
            min={5}
            max={200}
            step={5}
            marks={[
              { value: 5, label: '5' },
              { value: 50, label: '50' },
              { value: 100, label: '100' },
              { value: 150, label: '150' },
              { value: 200, label: '200' },
            ]}
            size="small"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showConfirmedOnly}
                onChange={(e) => setShowConfirmedOnly(e.target.checked)}
              />
            }
            label="Confirmed Only"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showEmptyCells}
                onChange={(e) => setShowEmptyCells(e.target.checked)}
              />
            }
            label="Show Empty Cells"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Health Threshold: {healthThreshold}
          </Typography>
          <Slider
            value={healthThreshold}
            onChange={(e, value) => setHealthThreshold(value)}
            min={0}
            max={100}
            step={10}
            marks={[
              { value: 0, label: '0' },
              { value: 40, label: '40' },
              { value: 60, label: '60' },
              { value: 80, label: '80' },
              { value: 100, label: '100' },
            ]}
            size="small"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Zoom Controls */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Zoom: {Math.round(zoomLevel * 100)}%
          </Typography>
          <ButtonGroup size="small" fullWidth>
            <Button onClick={handleZoomOut} startIcon={<ZoomOutIcon />}>
              Zoom Out
            </Button>
            <Button onClick={handleResetZoom} startIcon={<CenterIcon />}>
              Reset
            </Button>
            <Button onClick={handleZoomIn} startIcon={<ZoomInIcon />}>
              Zoom In
            </Button>
          </ButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<SelectAllIcon />}
            onClick={handleSelectAll}
          >
            Select All
          </Button>
          <Button
            size="small"
            startIcon={<CloseIcon />}
            onClick={handleClearSelection}
            disabled={selectedCount === 0}
          >
            Clear
          </Button>
        </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* Selection Actions */}
      {selectedCount > 0 && (
        <Paper
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            p: 2,
            zIndex: 1000,
          }}
          elevation={8}
        >
          <Alert severity="info" sx={{ mb: 2 }}>
            {selectedCount} relationships selected ({confirmedCount} already confirmed)
          </Alert>

          <ButtonGroup>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckBoxIcon />}
              onClick={handleBatchConfirm}
              disabled={selectedCount === confirmedCount}
            >
              Confirm Selected ({selectedCount - confirmedCount})
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleBatchReject}
            >
              Reject Selected
            </Button>
          </ButtonGroup>
        </Paper>
      )}

      {/* Legend */}
      <Paper
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          p: 2,
          zIndex: 1000,
          maxWidth: 200,
        }}
        elevation={3}
      >
        <Typography variant="subtitle2" gutterBottom>
          Legend
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Use mouse wheel to zoom, drag to pan
        </Typography>

        {!showEmptyCells && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Only showing entity pairs with relationships
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: getFaanaHealthColor(80),
                border: '1px solid #fff',
              }}
            />
            <Typography variant="caption">Relationship exists</Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: '#f5f5f5',
                border: '1px solid #fff',
              }}
            />
            <Typography variant="caption">No relationship</Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: '#4CAF50',
                border: '1px solid #fff',
              }}
            />
            <Typography variant="caption">Confirmed</Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#FF9800',
                border: '1px solid #fff',
              }}
            />
            <Typography variant="caption">Unconfirmed</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Hovered Cell Info */}
      {hoveredCell && hoveredCell.relationship && (
        <Paper
          sx={{
            position: 'absolute',
            top: 200,
            right: 16,
            p: 2,
            zIndex: 1001,
            maxWidth: 250,
          }}
          elevation={8}
        >
          <Typography variant="subtitle2" gutterBottom>
            {hoveredCell.relationship.relationshipType}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {hoveredCell.sourceEntity.name} → {hoveredCell.targetEntity.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Chip
              label={`Health: ${hoveredCell.relationship.overallHealth || 0}`}
              size="small"
              sx={{
                backgroundColor: getFaanaHealthColor(hoveredCell.relationship.overallHealth || 0),
                color: getContrastText(getFaanaHealthColor(hoveredCell.relationship.overallHealth || 0)),
              }}
            />
            <Chip
              label={hoveredCell.relationship.confidence}
              size="small"
              variant="outlined"
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Click to view details • Shift+click to select • Right-click for actions
          </Typography>
        </Paper>
      )}

      {/* SVG Container */}
      <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <svg ref={svgRef} style={{ display: 'block', cursor: 'move' }} />
      </Box>

      {/* Context Menu */}
      <Menu
        open={Boolean(contextMenu)}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextMenu && !contextMenu.relationship.isConfirmed && (
          <MenuItem
            onClick={() => {
              confirmRelationship(contextMenu.relationship.id, 'context-menu');
              setContextMenu(null);
            }}
          >
            Confirm Relationship
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setSelectedRelationship(contextMenu.relationship);
            setContextMenu(null);
          }}
        >
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            rejectRelationship(contextMenu.relationship.id);
            setContextMenu(null);
          }}
        >
          Reject Relationship
        </MenuItem>
      </Menu>

      {/* Relationship Detail Panel */}
      {selectedRelationship && (
        <RelationshipDetailPanel
          relationship={selectedRelationship}
          onClose={() => setSelectedRelationship(null)}
          onConfirm={() => {
            confirmRelationship(selectedRelationship.id, 'detail-panel');
            setSelectedRelationship(null);
          }}
          onReject={() => {
            rejectRelationship(selectedRelationship.id);
            setSelectedRelationship(null);
          }}
        />
      )}
    </Box>
  );
}

export default React.memo(RelationshipMatrix);